/**
 * Editor adapter over the canonical block catalog in trace-viewer.
 *
 * Node sockets, categories, and config fields are defined once in
 * `trace-viewer/src/behaviors/behaviorgraph/blockCatalog.ts` so docs, the AI
 * prompt, and editor palettes all stay in sync. This module re-exports that
 * catalog under the names this editor already uses, plus the palette colors
 * that are editor-specific.
 */

import {
    BLOCK_DEFS,
    getBlockDef,
    INTERVAL_MACRO_OP,
    type BlockDef,
    type BlockCategory,
    type SocketDef as CatalogSocketDef,
    type SocketType as CatalogSocketType,
    type ConfigFieldDef as CatalogConfigFieldDef,
} from 'trace-viewer/behaviors';

export type SocketType = CatalogSocketType;
export type NodeCategory = BlockCategory | 'port';
export type SocketDef = CatalogSocketDef;
export type ConfigFieldDef = CatalogConfigFieldDef;
export type NodeDef = BlockDef;

/**
 * Colors for node categories (editor-only — palette and handle styling).
 * `macro` is not a category — `registration: 'macro'` defs keep their real
 * category (Interval is execution) but get this distinct treatment because
 * they are compositions: one logical block backed by an expansion of standard
 * nodes (trace-viewer macros.ts).
 */
export const NODE_COLORS: Record<NodeCategory | 'macro', string> = {
    event: '#c05621',     // orange
    execution: '#2563eb', // blue
    data: '#16a34a',      // green
    subgraph: '#7c3aed',  // violet
    port: '#e11d48',      // rose
    macro: '#c026d3',     // fuchsia — composition blocks
};

/** Badge glyph marking composition (macro) blocks in the palette and node headers. */
export const MACRO_BADGE = '⧉'; // ⧉ two joined squares

/**
 * Initial inline literal values when placing a node from the palette.
 * Interval gets a concrete 1-second period: an empty/zero period never arms
 * the loop (commit-time lowering guard in trace-viewer macros.ts), which would
 * make a freshly placed node silently dead.
 */
export function defaultInlineValues(def: NodeDef): Record<string, unknown[]> {
    return def.op === INTERVAL_MACRO_OP ? { interval: [1] } : {};
}

/**
 * Editor palette definitions — the canonical viewer block catalog. `trace/setState`
 * is now a single scoped node (it fans out to the whole state group at runtime —
 * STATE_SCOPE_SPEC.md), so there is no editor-only "Set Group State" virtual node
 * anymore.
 */
export const NODE_DEFS: NodeDef[] = [...BLOCK_DEFS];

/** Lookup a node definition by op. */
export function getNodeDef(op: string): NodeDef | undefined {
    return getBlockDef(op);
}
